import { makeAutoObservable, runInAction } from "mobx";
import {
  BookingData,
  BookingDetailsData,
  BookingStatus,
  PartnerPaymentData,
  PaymentData,
  ServiceSales,
} from "../../types/booking";
import { PaginationData, PagingParams } from "../../types/pagination";
import { ActionResult } from "../../types";
import agent from "../api/agent";
import { BookingSchema } from "../../lib/schemas/bookingSchema";
import {
  CanceledReason,
  convertEnumToString,
  formatDateTime,
  paymentType,
  RefundStatus,
} from "../../lib/utils";
import { DocumentBookingSchema } from "../../lib/schemas/documentBookingSchema";
import { KsaBookingFormSchema } from "../../lib/schemas/ksaBookingSchema";
import { ClientData } from "../../types/client";
import { RefundRequestFormData } from "../../lib/schemas/refundRequestSchema";

export default class BookingStore {
  bookings: BookingData[] | null | undefined = null;
  // bookingsForUber: BookingData[] | null | undefined = null;
  currentBooking: BookingDetailsData | null = null;
  pagination: PaginationData | null = null;
  pagingParams = new PagingParams();
  loadingInitial = false;
  availableSlots: string[] | null = null;
  allBookings: BookingData[] = []; // ✅ used for full reports without pagination
  salesByService: ServiceSales[] = [];

  // New filter parameters
  searchTerm: string | null = null;
  bookingStatus: string | null = null;
  paymentStatus: string | null = null;
  fromDate: string | null = null;
  toDate: string | null = null;
  serviceId: string | null = null;
  bookingCode: string | null = null;

  invoiceNo: string | null = null;
  isSession: string[] | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // === Airport Cashier Bookings ===
  loadAirportCashierBookings = async () => {
    this.loadingInitial = true;
    try {
      const salesData = await agent.Bookings.getForAirportCashier(); // API call
      runInAction(() => {
        this.salesByService = salesData;
      });
    } catch (error) {
      console.error("Failed to load sales data:", error);
    } finally {
      runInAction(() => {
        this.loadingInitial = false;
      });
    }
  };

  calculateRemainingDays = async (
    client: ClientData
  ): Promise<number | null> => {
    if (!client.entryDateToUAE || !client.bookingId) return null;

    try {
      const booking = await this.getBooking(client.bookingId);
      if (!booking || typeof booking.duration !== "number") return null;

      const entryDate = new Date(client.entryDateToUAE);
      const visaEndDate = new Date(entryDate);
      visaEndDate.setDate(entryDate.getDate() + booking.duration);

      const today = new Date();
      const diffTime = visaEndDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return diffDays >= 0 ? diffDays : 0;
    } catch (error) {
      console.error("Error calculating remaining days:", error);
      return null;
    }
  };

  createRefundRequest = (refundRequest: RefundRequestFormData) => {
    return agent.Bookings.createRequest(refundRequest); // or however your agent is set up
  };

  addPayment = (paymentData: PaymentData) => {
    return agent.Bookings.addPayment(paymentData); // or however your agent is set up
  };

  addPartnerPayment = (paymentData: PartnerPaymentData) => {
    return agent.Bookings.addPartnerPayment(paymentData); // or however your agent is set up
  };

  // Add Booking
  addBooking = async (
    booking: BookingSchema | DocumentBookingSchema | KsaBookingFormSchema
  ): Promise<ActionResult<string>> => {
    try {
      const response = await agent.Bookings.create(booking);
      runInAction(() => {
        this.bookings = this.bookings
          ? [...this.bookings, response]
          : [response];
      });

      const sessionId = this.getOrCreateSessionId();

      const storedSessionData = JSON.parse(
        localStorage.getItem(sessionId) || '{"value": []}'
      );
      const storedBookings: string[] = storedSessionData.value || [];

      if (!storedBookings.includes(response.id)) {
        storedBookings.push(response.id);
      }

      this.setSessionData(sessionId, storedBookings);

      return { status: "success", data: response.id };
    } catch (error) {
      console.error("Error adding booking: ", error);
      return { status: "error", error: error as string };
    }
  };

  // uploadImage = async (formData: FormData): Promise<ActionResult<string>> => {
  //   try {
  //     await agent.Bookings.uploadImage(formData);
  //     return { status: "success", data: "Documents Uploaded Successfully" };
  //   } catch (error) {
  //     console.error("Error uploading Documents: ", error);
  //     return { status: "error", error: error as string };
  //   }
  // };

// bookingStore.ts
uploadBookingFiles = async (entityId: string, files: any[]): Promise<ActionResult<string>> => {
  try {
    const formData = new FormData();

    // must match backend dto: EntityId + Files
    formData.append("EntityId", entityId);

    files.forEach((f: any) => {
      const fileObj = f?.originFileObj ?? f; // supports AntD fileList or raw File[]
      if (fileObj) formData.append("Files", fileObj);
    });

    const result = await agent.Bookings.uploadBookingFiles(formData);

    return { status: "success", data: result || "Files uploaded successfully" };
  } catch (err: any) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      (typeof err?.response?.data === "string" ? err.response.data : null) ||
      err?.message ||
      "Upload failed";

    console.error("Error uploading files:", err?.response?.data || err);

    return { status: "error", error: msg };
  }
};

uploadImage = async (formData: FormData): Promise<ActionResult<string>> => {
  try {
    await agent.Bookings.uploadImage(formData);
    return { status: "success", data: "Documents Uploaded Successfully" };
  } catch (err: any) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      (typeof err?.response?.data === "string" ? err.response.data : null) ||
      err?.message ||
      "Upload failed";

    console.error("Error uploading documents:", err?.response?.data || err);

    return { status: "error", error: msg };
  }
};



  setSessionData = (sessionId: string, data: string[]) => {
    const expiryTime = new Date().getTime() + 20 * 60 * 1000; // 20 minutes from now
    const sessionData = {
      value: data,
      expiry: expiryTime,
    };
    localStorage.setItem(sessionId, JSON.stringify(sessionData));
  };
  getOrCreateSessionId = (): string => {
    let sessionId = localStorage.getItem("sessionId");
    if (!sessionId) {
      sessionId = this.generateUniqueId();
      localStorage.setItem("sessionId", sessionId);
    }
    return sessionId;
  };

  getExistingSessionId = (): string | null => {
    return localStorage.getItem("sessionId");
  };
  getCurrentSessionBookings = (): string[] | null => {
    const sessionId = this.getExistingSessionId();
    if (!sessionId) return null;

    this.removeExpiredSessions(sessionId);

    const storedSessionData = localStorage.getItem(sessionId);
    if (storedSessionData) {
      const parsedSessionData = JSON.parse(storedSessionData);
      const currentSessionBookings = parsedSessionData.value;
      this.isSession = currentSessionBookings;
      Object.keys(localStorage)
        .filter((key) => key !== "sessionId" && key !== "language")
        .forEach((id) => this.removeExpiredSessions(id));

      return currentSessionBookings; // ✅ Important to return it
    }

    return null;
  };

  removeBookingSession = () => {
    const sessionId = this.getExistingSessionId();

    if (sessionId) {
      localStorage.removeItem(sessionId);
      localStorage.removeItem("sessionId");
    }

    this.isSession = null;
  };

  removeExpiredSessions = (sessionId: string) => {
    const sessionData = localStorage.getItem(sessionId);
    if (sessionData) {
      const { expiry } = JSON.parse(sessionData);
      const currentTime = new Date().getTime();

      if (currentTime > expiry) {
        localStorage.removeItem(sessionId);
      }
    }
  };

  // Utility method to generate a unique ID (can use a library like UUID)
  generateUniqueId = (): string => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0,
        v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  // Delete Booking
  deleteBooking = async (id: string): Promise<ActionResult<string>> => {
    try {
      await agent.Bookings.delete(id);
      runInAction(() => {
        this.bookings = this.bookings?.filter((b) => b.id !== id) || null;
      });
      return { status: "success", data: "Booking deleted successfully" };
    } catch (error) {
      console.error("Error deleting booking: ", error);
      return { status: "error", error: error as string };
    }
  };

  loadAllBookings = async () => {
    try {
      const response = await agent.Bookings.getAllWithoutPagination(); // Ensure this hits /booking/all
      runInAction(() => {
        this.allBookings = response; // This should be an array (not paginated)
      });
    } catch (error) {
      console.error("Failed to load all bookings", error);
    }
  };

  // Load Bookings
  loadBookings = async () => {
    const bookingList: BookingData[] = [];
    try {
      const params = {
        pageNumber: this.pagingParams.pageNumber,
        pageSize: this.pagingParams.pageSize,
        searchTerm: this.searchTerm,
        bookingStatus: this.bookingStatus,
        paymentStatus: this.paymentStatus, // ✅ string sent to backend
        from: this.fromDate,
        to: this.toDate,
        serviceId: this.serviceId,
        bookingCode: this.bookingCode,
        invoiceNo: this.invoiceNo,
      };

      //const result = await agent.Bookings.getAll(params);
      const result = await agent.Bookings.getAll(this.axiosParams);

      runInAction(() => {
        const { pageNumber, pageSize, data, pageCount, totalCount } = result;
        this.setPagination({ pageNumber, pageSize, pageCount, totalCount });

        data.map((item) => {
          bookingList.push({
            ...item,
            updateDate: item.updateDate
              ? formatDateTime(item.updateDate)
              : "No set",
            createDate: formatDateTime(item.createDate),
            updatedBy: item.updatedBy ? item.updatedBy : "No set",
            bookingDate: formatDateTime(item.bookingDate?.toString()),
            totalPrice: item.totalPrice + " AED",
            bookingStatus: convertEnumToString(
              Number(item.bookingStatus),
              BookingStatus
            ),
            paymentStatus: item.paymentStatus,
          });
        });

        this.bookings = bookingList;
      });
      this.loadingInitial = false;
    } catch (error) {
      console.error("Error loading bookings: ", error);
      this.loadingInitial = false;
    }
  };

  getReport = async () => {
    const bookingList: BookingData[] = [];
    try {
      const result = await agent.Bookings.getReport(this.axiosParams);

      runInAction(() => {
        const { pageNumber, pageSize, data, pageCount, totalCount } = result;

        this.setPagination({ pageNumber, pageSize, pageCount, totalCount });

        if (Array.isArray(data)) {
          data.forEach((item) => {
            bookingList.push({
              ...item,
              updateDate: item.updateDate
                ? formatDateTime(item.updateDate)
                : "No set",
              bookingDate: formatDateTime(item.bookingDate?.toString()),
            });
          });
        } else {
          console.warn("Expected data array but got:", data);
        }

        this.bookings = bookingList;
      });

      this.loadingInitial = false;
    } catch (error) {
      console.error("Error loading bookings: ", error);
      this.loadingInitial = false;
    }
  };

  // loadBookingsForUber = async () => {
  //   const bookingList: BookingData[] = [];
  //   try {
  //     const result = await agent.Bookings.getAllForUber();
  //     runInAction(() => {

  //       result.map((item) => {
  //         bookingList.push({
  //           ...item,
  //           updateDate: item.updateDate
  //             ? formatDateTime(item.updateDate)
  //             : "No set",
  //           createDate: formatDateTime(item.createDate),
  //           updatedBy: item.updatedBy ? item.updatedBy : "No set",
  //           bookingDate: formatDateTime(item.bookingDate?.toString()),
  //           totalPrice: item.totalPrice + " AED",
  //           bookingStatus: convertEnumToString(
  //             Number(item.bookingStatus),
  //             BookingStatus
  //           ),
  //         });
  //       });

  //       this.bookingsForUber = bookingList;
  //     });
  //     this.loadingInitial = false;
  //   } catch (error) {
  //     console.error("Error loading bookings: ", error);
  //     this.loadingInitial = false;
  //   }
  // };

  // CalculateRemainingDays = async (client: ClientData): Promise<number | null> => {
  //   if (!client.entryDateToUAE || !client.bookingId) return null;

  //   try {
  //     const booking = await this.getBooking(client.bookingId); // `this.getBooking` is your method
  //     if (!booking || typeof booking.duration !== "number") return null;

  //     const entryDate = new Date(client.entryDateToUAE);
  //     const visaEndDate = new Date(entryDate);
  //     visaEndDate.setDate(entryDate.getDate() + booking.duration);

  //     const today = new Date();
  //     const diffTime = visaEndDate.getTime() - today.getTime();
  //     const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  //     return diffDays >= 0 ? diffDays : 0;
  //   } catch (error) {
  //     console.error("Error calculating remaining days:", error);
  //     return null;
  //   }
  // };

  // Get Booking by ID
  getBooking = async (id: string) => {
    try {
      const result = await agent.Bookings.getById(id);

      runInAction(() => {
        this.currentBooking = {
          ...result,
          bookingStatus: convertEnumToString(
            Number(result.bookingStatus),
            BookingStatus
          ),
        };
      });
      return this.currentBooking;
    } catch (error) {
      console.error("Error fetching booking: ", error);
      return null;
    }
  };

  getBookingByCustomerId = async (id: string) => {
    const bookingList: BookingData[] = [];
    try {
      const result = await agent.Bookings.getBookingByCustomerId(id);
      runInAction(() => {
        result.map((item) => {
          bookingList.push({
            ...item,
            updateDate: item.updateDate
              ? formatDateTime(item.updateDate)
              : "No set",
            createDate: formatDateTime(item.createDate),
            updatedBy: item.updatedBy ? item.updatedBy : "No set",
            bookingDate: formatDateTime(item.bookingDate?.toString()),
            totalPrice: item.totalPrice + " AED",
            bookingStatus: convertEnumToString(
              Number(item.bookingStatus),
              BookingStatus
            ),
          });
        });

        this.bookings = bookingList;
      });
      this.loadingInitial = false;
      return bookingList;
    } catch (error) {
      console.error("Error loading bookings: ", error);
      this.loadingInitial = false;
    }
  };

  getBookingByEmail = async (email: string) => {
    const bookingList: BookingData[] = [];
    try {
      const result = await agent.Bookings.getBookingByEmail(email);
      runInAction(() => {
        result.map((item) => {
          bookingList.push({
            ...item,
            updateDate: item.updateDate
              ? formatDateTime(item.updateDate)
              : "No set",
            createDate: formatDateTime(item.createDate),
            updatedBy: item.updatedBy ? item.updatedBy : "No set",
            bookingDate: formatDateTime(item.bookingDate?.toString()),
            totalPrice: item.totalPrice + " AED",
            bookingStatus: convertEnumToString(
              Number(item.bookingStatus),
              BookingStatus
            ),
          });
        });

        this.bookings = bookingList;
      });
      this.loadingInitial = false;
      return bookingList;
    } catch (error) {
      console.error("Error loading bookings: ", error);
      this.loadingInitial = false;
    }
  };

  // Get Available Slots
  getAvailableSlots = async (date: string): Promise<ActionResult<string>> => {
    try {
      const result = await agent.Bookings.getAvailableSlots(date);
      runInAction(() => {
        this.availableSlots = result;
      });
      return {
        status: "success",
        data: "Received available slots successfully",
      };
    } catch (error) {
      runInAction(() => {
        this.availableSlots = null;
      });
      console.error("Error loading available slots: ", error);
      return { status: "error", error: error as string };
    }
  };

  // Update Booking Status
  setPaymentTypeOfBooking = async (
    id: string,
    type: paymentType
  ): Promise<ActionResult<string>> => {
    try {
      await agent.Bookings.setPaymentTypeOfBooking(id, type);
      await this.getBooking(id);
      return { status: "success", data: `Booking Payment Type set to ${type}` };
    } catch (error) {
      console.error("Error updating booking Payment Type: ", error);
      return { status: "error", error: error as string };
    }
  };

  updateRefundRequestStatus = async (
    refundRequestId: string,
    status: RefundStatus
  ): Promise<ActionResult<string>> => {
    try {
      await agent.Bookings.updateRefundStatus(refundRequestId, { status });

      // Refresh the current booking if it contains this refund request
      if (
        this.currentBooking?.refundRequests?.some(
          (r) => r.id === refundRequestId
        )
      ) {
        await this.getBooking(this.currentBooking.id);
      }

      return {
        status: "success",
        data: `Refund request status updated to ${status}`,
      };
    } catch (error) {
      console.error("Error updating refund status: ", error);
      return {
        status: "error",
        error: (error as Error).message,
      };
    }
  };

  updateAdditionalPrice = async (
    bookingId: string,
    additionalPrice: number
  ): Promise<ActionResult<string>> => {
    try {
      // Make API call to update additional price
      await agent.Bookings.updateAdditionalPrice(bookingId, {
        additionalPrice,
      });

      // Refresh current booking data if we're updating the currently viewed booking
      if (this.currentBooking?.id === bookingId) {
        await this.getBooking(bookingId); // Backend will return updated totalPrice
      }

      return {
        status: "success",
        data: `Additional price updated successfully`,
      };
    } catch (error) {
      console.error("Error updating additional price: ", error);
      return {
        status: "error",
        error: (error as Error).message || "Failed to update additional price",
      };
    }
  };

  setStatusInProcess = async (id: string): Promise<ActionResult<string>> => {
    try {
      await agent.Bookings.setStatusInProcess(id);
      await this.getBooking(id);
      return { status: "success", data: "Booking status set to 'In Process'." };
    } catch (error) {
      console.error("Error updating booking status: ", error);
      return { status: "error", error: error as string };
    }
  };

  setStatusCanceled = async (
    id: string,
    reason: CanceledReason
  ): Promise<ActionResult<string>> => {
    try {
      await agent.Bookings.setStatusCanceled(id, reason);
      await this.getBooking(id);
      return { status: "success", data: "Booking status set to 'Canceled'." };
    } catch (error) {
      console.error("Error updating booking status: ", error);
      return { status: "error", error: error as string };
    }
  };

  setStatusPending = async (id: string): Promise<ActionResult<string>> => {
    try {
      await agent.Bookings.setStatusPending(id);
      await this.getBooking(id);
      return { status: "success", data: "Booking status set to 'Canceled'." };
    } catch (error) {
      console.error("Error updating booking status: ", error);
      return { status: "error", error: error as string };
    }
  };

  setStatusCompleted = async (id: string): Promise<ActionResult<string>> => {
    try {
      await agent.Bookings.setStatusCompleted(id);
      await this.getBooking(id);
      return { status: "success", data: "Booking status set to 'Completed'." };
    } catch (error) {
      console.error("Error updating booking status: ", error);
      return { status: "error", error: error as string };
    }
  };

  // Set Pagination
  setPagination = (pagination: PaginationData) => {
    this.pagination = pagination;
  };

  // Axios Parameters with filters
  get axiosParams() {
    const params = new URLSearchParams();
    params.append("pageNumber", this.pagingParams.pageNumber.toString());
    params.append("pageSize", this.pagingParams.pageSize.toString());
    if (this.bookingStatus) params.append("bookingStatus", this.bookingStatus);
    if (this.paymentStatus) params.append("paymentStatus", this.paymentStatus);
    if (this.fromDate) params.append("from", this.fromDate);
    if (this.toDate) params.append("to", this.toDate);
    if (this.serviceId) params.append("serviceId", this.serviceId);
    if (this.bookingCode) params.append("bookingCode", this.bookingCode);

    if (this.invoiceNo) params.append("invoiceNo", this.invoiceNo);
    if (this.searchTerm) params.append("searchTerm", this.searchTerm);
    return params;
  }

  setBookingCodeFilter = (bookingCode: string | null) => {
    this.bookingCode = bookingCode;
  };
  setInvoiceNoFilter = (invoiceNo: string | null) => {
    this.invoiceNo = invoiceNo;
  };

  // New methods to set filters
  setStatusFilter = (status: string | null) => {
    this.bookingStatus = status;
  };

  setPaymentstatusFilter = (status: string | null) => {
    this.paymentStatus = status;
  };

  setDateFilter = (from: string | null, to: string | null) => {
    this.fromDate = from;
    this.toDate = to;
  };

  setServiceFilter = (serviceId: string | null) => {
    this.serviceId = serviceId;
  };

  setSearchTerm = (term: string) => {
    this.searchTerm = term;
  };

  clearBookings = () => {
    this.bookings = null;
  };

  setPagingParams = (pagingParams: PagingParams) => {
    this.pagingParams = pagingParams;
  };
}
